import { useDraftReturn } from "./use-draft-return";
import { usePublicOrder } from "../queries/use-public-order";
import { usePublicShop } from "../queries/use-public-shop";

/**
 * Computes the bonus amount (in cents) added to the discount for a given line item — §25:
 * - `percentage`: `paidPrice * bonus.amount / 100`
 * - `fixed_amount`: `bonus.amount` (store currency) **once per return** — only the first
 *   discount item receives the bonus; subsequent items get 0.
 *
 * Returns null when no bonus is configured.
 */
export function useDiscountBonus(
  orderId: string,
  shopId: string,
  lineItemId: string,
): number | null {
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const draft = useDraftReturn(orderId);

  const bonus = shop?.discount?.discount_bonus;
  if (!bonus || !bonus.amount || bonus.amount === 0) return null;

  if (bonus.type === "percentage") {
    const lineItem = (order?.lineItems as any[])?.find(
      (li: any) => li.line_item_id === lineItemId,
    );
    const paidPriceCents = Math.round(parseFloat(lineItem?.price ?? "0") * 100);
    return Math.round((paidPriceCents * bonus.amount) / 100);
  }

  if (bonus.type === "fixed_amount") {
    // Only the first item with a discount resolution gets the fixed bonus.
    const firstDiscountLineItemId = draft?.lineItems.find(
      (li) => li.resolution === "discount",
    )?.lineItemId;
    if (firstDiscountLineItemId !== lineItemId) return 0;
    return Math.round(bonus.amount * 100);
  }

  return null;
}
